import { useEffect, useState } from 'react';

import type { ColumnDraft, FilteredColumnRow } from '../types';

type Params = {
  columns: ColumnDraft[];
  filteredColumns: FilteredColumnRow[];
};

export function useColumnSelection({ columns, filteredColumns }: Params) {
  const [selectedNames, setSelectedNames] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (selectedNames.size === 0) return;
    const valid = new Set(columns.map((c) => c.name));
    let changed = false;
    const next = new Set<string>();
    for (const n of selectedNames) {
      if (valid.has(n)) next.add(n);
      else changed = true;
    }
    if (changed) setSelectedNames(next);
  }, [columns, selectedNames]);

  const filteredAllSelected = filteredColumns.length > 0 && filteredColumns.every(({ col }) => selectedNames.has(col.name));

  const toggleRowSelection = (name: string, checked: boolean) => {
    setSelectedNames((prev) => {
      const next = new Set(prev);
      if (checked) next.add(name);
      else next.delete(name);
      return next;
    });
  };

  const toggleSelectAllFiltered = (checked: boolean) => {
    setSelectedNames((prev) => {
      const next = new Set(prev);
      if (checked) {
        for (const { col } of filteredColumns) next.add(col.name);
      } else {
        for (const { col } of filteredColumns) next.delete(col.name);
      }
      return next;
    });
  };

  const clearColumnSelection = () => setSelectedNames(new Set());

  return {
    selectedNames,
    filteredAllSelected,
    toggleRowSelection,
    toggleSelectAllFiltered,
    clearColumnSelection,
  };
}
